import React, { useState, useRef } from "react";
import styles from "./ElementSelector.module.css";
import CulturalElementChat from "./CulturalElementChat";
import { track } from "../utils/analytics";
import { useLanguage } from "../contexts/LanguageContext";
import { t } from "../utils/translations";

const ElementSelector = ({ elements, grade, standard, language, moku, island, onSelectionChange, onContentChange, onNext }) => {
  const { language: uiLanguage } = useLanguage();
  const [selected, setSelected] = useState({
    story: false,
    proverb: false,
    cultural_practice: false,
  });

  const [loading, setLoading] = useState({
    story: false,
    proverb: false,
    cultural_practice: false,
  });

  const [expanded, setExpanded] = useState({
    story: false,
    proverb: false,
    cultural_practice: false,
  });

  const [content, setContent] = useState(elements);
  const [showCustomChat, setShowCustomChat] = useState(false);
  const mainContentRef = useRef(null);

  const toggleExpanded = (type) => {
    setExpanded(prev => ({ ...prev, [type]: !prev[type] }));
  };

  // Track generation history per category
  const [generationHistory, setGenerationHistory] = useState({
    story: [],
    proverb: [],
    cultural_practice: [],
  });

  // Track current generation index per category
  const [currentGenerationIndex, setCurrentGenerationIndex] = useState({
    story: 0,
    proverb: 0,
    cultural_practice: 0,
  });

  // Track which version index was selected per type (blue only on that version)
  const [selectedVersionIndex, setSelectedVersionIndex] = useState({
    story: null,
    proverb: null,
    cultural_practice: null,
  });

  // Use refs to store latest state for calculations
  const historyRef = useRef(generationHistory);
  const indexRef = useRef(currentGenerationIndex);

  // Keep refs in sync with state
  React.useEffect(() => {
    historyRef.current = generationHistory;
  }, [generationHistory]);

  React.useEffect(() => {
    indexRef.current = currentGenerationIndex;
  }, [currentGenerationIndex]);

  // Track the initial elements ID to detect when we get a completely new set of elements
  const elementsIdRef = useRef(null);
  
  // Initialize generation history when elements change (only on initial load or new lesson plan)
  React.useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, []);

  React.useEffect(() => {
    if (elements) {
      // Check if this is a completely new set of elements (different ID) or first load
      const currentElementsId = elements.id || JSON.stringify(elements);
      const isNewElements = elementsIdRef.current !== currentElementsId;
      
      if (isNewElements) {
        if (mainContentRef.current) {
          mainContentRef.current.scrollTop = 0;
        }
        setContent(elements);
        // Initialize history with current elements
        const initialHistory = {
          story: elements.story ? [elements.story] : [],
          proverb: elements.proverb ? [elements.proverb] : [],
          cultural_practice: elements.cultural_practice ? [elements.cultural_practice] : [],
        };
        const initialIndex = {
          story: 0,
          proverb: 0,
          cultural_practice: 0,
        };
        setGenerationHistory(initialHistory);
        setCurrentGenerationIndex(initialIndex);
        setSelectedVersionIndex({ story: null, proverb: null, cultural_practice: null });
        // Update refs immediately
        historyRef.current = initialHistory;
        indexRef.current = initialIndex;
        elementsIdRef.current = currentElementsId;
        console.log('Initialized history for new elements:', currentElementsId);
      }
    }
  }, [elements]);

  const toggleSelect = (type) => {
    const nowSelected = !selected[type];
    const updated = {
      ...selected,
      [type]: nowSelected,
    };
    setSelected(updated);
    setSelectedVersionIndex((prev) => ({
      ...prev,
      [type]: nowSelected ? indexRef.current[type] : null,
    }));
    if (onSelectionChange) onSelectionChange(updated);
  };

  const regenerateElement = async (type) => {
    const labels = { story: 'Story', proverb: 'ʻŌlelo Noʻeau', cultural_practice: 'Cultural Practice' };
    const lessonPlanId = content?.id ?? elements?.id;
    track(`Clicked Regenerate ${labels[type] || type}`, { lesson_plan_id: lessonPlanId, element_type: type, screen_name: 'Select Elements' });
    setLoading((prev) => ({ ...prev, [type]: true }));

    try {
      if (!lessonPlanId) {
        alert("Missing lesson plan ID. Please go back to the previous step and generate elements again.");
        return;
      }
      const token = localStorage.getItem('authToken');
      // Get current element for this type
      const currentElement = content?.[type];
      
      const response = await fetch(`/api/regenerate-cultural-elements/`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...(token && { 'Authorization': `Token ${token}` }),
        },
        body: JSON.stringify({
          id: lessonPlanId,
          grade: grade,
          standard: standard,
          language: language,
          cultural_element: type,
          existing_cultural_element : currentElement,      
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        const message = data?.error || "Failed to regenerate.";
        throw new Error(message);
      }

      // Backend returns e.g. { story: {...} } or { proverb: {...} } or { cultural_practice: {...} }
      const newElement = data[type];
      if (newElement == null) {
        console.warn("Regenerate response missing key for type:", type, "Response keys:", Object.keys(data));
        alert("Received an unexpected response from the server. Please try again.");
        return;
      }
      
      // Update generation history: use refs to get latest state, then update both states
      const currentHistory = historyRef.current[type] || [];
      const currentIndex = indexRef.current[type];
      
      let newHistory;
      let newIndex;
      
      // If we're at index 0 (showing original), keep original at index 0, add new at index 1
      // If we're at a later index, the current element is already in history, just add new at end
      if (currentIndex === 0) {
        // Original stays at 0, new goes to 1
        if (currentHistory.length > 0) {
          newHistory = [currentHistory[0], newElement];
        } else {
          // If history is empty, use current element as original
          newHistory = [currentElement, newElement];
        }
        newIndex = 1;
      } else {
        // Current element is already in history, add new at end
        newHistory = [...currentHistory, newElement];
        newIndex = newHistory.length - 1;
      }
      
      // Update both states - React will batch these updates
      setGenerationHistory((prev) => {
        const updated = { ...prev, [type]: newHistory };
        historyRef.current = updated; // Update ref immediately
        console.log(`[${type}] Regenerate - Updated history:`, newHistory, 'length:', newHistory.length, 'Full history:', updated);
        return updated;
      });
      
      setCurrentGenerationIndex((prev) => {
        const updated = { ...prev, [type]: newIndex };
        indexRef.current = updated; // Update ref immediately
        console.log(`[${type}] Regenerate - Updated index to:`, newIndex, 'History length:', newHistory.length, 'Full index:', updated);
        return updated;
      });
      
      // Update content to show new element (preserve id for future regenerates)
      const updatedContent = { ...content, id: lessonPlanId, [type]: newElement };
      setContent(updatedContent);

      if (onContentChange) onContentChange(updatedContent);

    } catch (error) {
      const message = error?.message || "Error while regenerating. Please try again.";
      alert(message);
      console.error("Regenerate cultural element error:", error);
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  // Navigate through generation history for a specific category
  const navigateGeneration = (type, direction) => {
    const history = historyRef.current[type] || [];
    const currentIndex = indexRef.current[type];
    
    if (history.length === 0) return;
    
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = Math.max(0, currentIndex - 1);
    } else {
      newIndex = Math.min(history.length - 1, currentIndex + 1);
    }
    
    if (newIndex !== currentIndex && history[newIndex]) {
      // Update content to show the element at newIndex
      const elementToShow = history[newIndex];
      setContent((prevContent) => {
        const updatedContent = { ...prevContent, [type]: elementToShow };
        if (onContentChange) onContentChange(updatedContent);
        return updatedContent;
      });
      
      // Update index
      setCurrentGenerationIndex((prev) => {
        const updated = { ...prev, [type]: newIndex };
        indexRef.current = updated; // Update ref immediately
        return updated;
      });
    }
  };

  const handleUseOwnConnection = () => {
    setShowCustomChat(true);
  };

  React.useEffect(() => {
    if (!showCustomChat) return;
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [showCustomChat]);

  // Helper function to check if two elements are the same
  const areElementsEqual = (elem1, elem2, type) => {
    if (!elem1 || !elem2) return false;
    if (type === 'story') {
      return elem1.mo_olelo_name === elem2.mo_olelo_name && 
             elem1.mo_olelo === elem2.mo_olelo;
    } else if (type === 'proverb') {
      return elem1.olelo_noeau === elem2.olelo_noeau;
    } else if (type === 'cultural_practice') {
      return elem1.cultural_practice_name === elem2.cultural_practice_name;
    }
    return JSON.stringify(elem1) === JSON.stringify(elem2);
  };

  // Helper function to find element index in history
  const findElementIndexInHistory = (history, elementData, type) => {
    return history.findIndex(item => areElementsEqual(item, elementData, type));
  };

  const handleElementAccepted = (element) => {
    // Add the custom element to content
    const elementType = element.type;
    const elementData = element.data;
    
    // Get current state using refs to ensure we have the latest
    const currentHistory = historyRef.current[elementType] || [];
    const existingElement = content?.[elementType];
    
    // Check if the new element already exists in history
    const existingIndex = findElementIndexInHistory(currentHistory, elementData, elementType);
    
    let newHistory;
    let newIndex;
    
    if (existingIndex !== -1) {
      // Element already exists in history, just navigate to it
      newHistory = currentHistory;
      newIndex = existingIndex;
    } else {
      // Element doesn't exist, add it to history
      newHistory = [...currentHistory];
      
      // If there's an existing element in content that's different and not in history, add it first
      if (existingElement && !areElementsEqual(existingElement, elementData, elementType)) {
        const existingInHistory = findElementIndexInHistory(newHistory, existingElement, elementType);
        if (existingInHistory === -1) {
          // Existing element is not in history, add it first
          newHistory.push(existingElement);
        }
      }
      
      // Add the new element
      newHistory.push(elementData);
      newIndex = newHistory.length - 1;
    }
    
    // Update generation history
    setGenerationHistory((prev) => {
      const updated = { ...prev, [elementType]: newHistory };
      historyRef.current = updated;
      return updated;
    });
    
    // Update current index to point to the new element
    setCurrentGenerationIndex((prevIndex) => {
      const updatedIndex = {
        ...prevIndex,
        [elementType]: newIndex
      };
      indexRef.current = updatedIndex;
      return updatedIndex;
    });
    
    // Update content with the new element
    const updatedContent = { ...content, [elementType]: elementData };
    setContent(updatedContent);
    
    // Automatically select the newly added element (this version is the selected one)
    const updatedSelection = {
      ...selected,
      [elementType]: true
    };
    setSelected(updatedSelection);
    setSelectedVersionIndex((prev) => ({
      ...prev,
      [elementType]: newIndex,
    }));

    // Notify parent of selection change
    if (onSelectionChange) {
      onSelectionChange(updatedSelection);
    }
    
    // Notify parent of content change
    if (onContentChange) {
      onContentChange(updatedContent);
    }
    
    // Keep the sidebar open so user can add more elements
    // setShowCustomChat(false); // Removed - keep sidebar open
  };

  const handleAddAnother = () => {
    // Reset chat state but keep panel open
    // The CulturalElementChat component will handle resetting its own messages
  };

  const getElementTitle = (type) => {
    switch(type) {
      case 'story': return t('story', uiLanguage);
      case 'proverb': return t('proverb', uiLanguage);
      case 'cultural_practice': return t('cultural_practice', uiLanguage);
      default: return "";
    }
  };

  const getElementDescription = (type) => {
    const element = content?.[type];
    if (!element) return "";
    
    switch(type) {
      case 'story': return element.mo_olelo || "";
      case 'proverb': {
        const olelo = element.olelo_noeau || "";
        const translation = element.translation || "";
        if (olelo && translation) {
          return `${olelo}\n\n${translation}`;
        }
        return translation || olelo || "";
      }
      case 'cultural_practice': return element.full_cultural_practice || "";
      default: return "";
    }
  };

  const getElementConnection = (type) => {
    return content?.[type]?.connection || "";
  };

  const cardTypes = ['story', 'proverb', 'cultural_practice'];

  const lessonPlanId = content?.id || elements?.id;

  return (
    <div className={`${styles.container} ${showCustomChat ? styles.withRightPanel : ''}`}>
      <div ref={mainContentRef} className={styles.mainContent}>
      <div className={styles.headerSection}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{t('select_elements_title', uiLanguage)}</h1>
          <p className={styles.subtitle}>
            {t('select_elements_subtitle', uiLanguage)}
          </p>
        </div>

        <div className={styles.carouselContainer}>
          <div className={styles.cardsWrapper}>
            {cardTypes.map((type) => {
              const currentIndex = currentGenerationIndex[type];
              const isSelectedVersion = selected[type] && currentIndex === selectedVersionIndex[type];
              const element = content?.[type];
              
              // Only show cards that have content
              if (!element) return null;
              
              const history = generationHistory[type] || [];
              const canNavigatePrev = history.length > 0 && currentIndex > 0;
              const canNavigateNext = history.length > 0 && currentIndex < history.length - 1;
              
              // Debug logging
              if (history.length > 0) {
                console.log(`[${type}] Render - History length:`, history.length, 'Current index:', currentIndex, 'Can navigate prev:', canNavigatePrev, 'Can navigate next:', canNavigateNext);
              }
              
              return (
                <div 
                  key={type}
                  className={`${styles.card} ${isSelectedVersion ? styles.selected : ''}`}
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.elementTypeLabel}>{getElementTitle(type)}</div>
                    <div className={styles.cardControls}>
                      <button
                        className={styles.cardNavButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateGeneration(type, 'prev');
                        }}
                        disabled={!canNavigatePrev}
                        title="Previous generation"
                      >
                        <div className={styles.caretLeftSmall} />
                      </button>
                      <button
                        className={styles.cardNavButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateGeneration(type, 'next');
                        }}
                        disabled={!canNavigateNext}
                        title="Next generation"
                      >
                        <div className={styles.caretRightSmall} />
                      </button>
                    </div>
                  </div>
                  
                  <div className={styles.description}>
                    {type === 'proverb' && content?.[type]?.olelo_noeau ? (
                      <>
                        <p className={`${styles.descriptionText} ${!expanded[type] ? styles.truncated : ''}`} style={{ fontWeight: '600', marginBottom: '8px' }}>
                          {content[type].olelo_noeau}
                        </p>
                        {content[type].translation && (
                          <p className={`${styles.descriptionText} ${!expanded[type] ? styles.truncated : ''}`}>
                            {content[type].translation}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className={`${styles.descriptionText} ${!expanded[type] ? styles.truncated : ''}`}>
                        {getElementDescription(type)}
                      </p>
                    )}
                    {(() => {
                      // Only show "Show more" button if content is long enough to be truncated
                      const description = getElementDescription(type);
                      const isLongContent = description.length > 250; // Threshold for showing button
                      
                      if (isLongContent) {
                        return (
                          <div className={styles.showMoreWrapper}>
                            <button
                              className={styles.showMoreButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(type);
                              }}
                            >
                              {expanded[type] ? 'Show less' : 'Show more'}
                            </button>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  <div className={styles.connectionSection}>
                    <div className={styles.connectionLabel}>Connection</div>
                    <p className={styles.connectionText}>{getElementConnection(type)}</p>
                  </div>

                  <div className={styles.cardActions}>
                    <button
                      className={styles.cardRegenerateButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        regenerateElement(type);
                      }}
                      disabled={loading[type]}
                    >
                      {loading[type] ? 'Regenerating...' : 'Regenerate'}
                    </button>
                    <button
                      className={styles.cardSelectButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(type);
                      }}
                    >
                      Select
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Sticky Next button under main content */}
      {onNext && (
        <div className={styles.stickyNextButtonWrapper}>
          <button 
            className={styles.nextButton}
            onClick={onNext}
            disabled={!Object.values(selected).some(val => val)}
          >
            <span>NEXT</span>
            <div className={styles.nextChevron}>
              <div className={styles.nextChevronIcon} />
            </div>
          </button>
        </div>
      )}

      {/* Close mainContent wrapper */}
      </div>

      {/* Kōkua Chat card – flush to right wall; opens custom connection chat */}
      <div className={styles.customConnectionCard}>
        <div className={styles.kokuaChatHeader}>
          <img src="/assets/Solid/Interface/AI.svg" alt="" className={styles.kokuaChatHeaderIcon} aria-hidden />
          <h3 className={styles.kokuaChatTitle}>{t('use_my_story', uiLanguage)}</h3>
        </div>
        <p className={styles.kokuaChatText}>
          {t('use_my_story_line1', uiLanguage)}
        </p>
        <p className={styles.kokuaChatText}>
          {t('use_my_story_line2', uiLanguage)}
        </p>
        <button
          type="button"
          className={styles.kokuaChatButton}
          onClick={handleUseOwnConnection}
          title="Open Kōkua Chat"
        >
          <img src="/assets/Solid/Interface/AI.svg" alt="" className={styles.kokuaChatButtonIcon} aria-hidden />
          <span>KŌKUA CHAT</span>
        </button>
      </div>

      {/* Right Panel for Custom Cultural Element Chat */}
      {showCustomChat && (
        <div className={styles.rightPanel}>
          <div className={styles.rightPanelHeader}>
            <h3 className={styles.rightPanelTitle}>Create Custom Connection</h3>
            <button 
              className={styles.closePanelButton}
              onClick={() => setShowCustomChat(false)}
              title="Close"
            >
              ✕
            </button>
          </div>
          <CulturalElementChat
            lessonPlanId={lessonPlanId}
            grade={grade}
            standard={standard}
            language={language}
            moku={moku}
            island={island}
            existingElements={content}
            onElementAccepted={handleElementAccepted}
            onAddAnother={handleAddAnother}
          />
        </div>
      )}
    </div>
  );
};

export default ElementSelector;
